/**
 * The client-side adapter interface of §4, plus its HTTP implementation.
 *
 * Two things this deliberately does not do:
 *   - it does not use Mock Service Worker for the fake (§4) — MSW intercepts with
 *     a Service Worker, and Service Workers do not exist in the iOS Capacitor
 *     webview, so offline behaviour tested against it would not transfer;
 *   - it does not accept a relative base URL (§13.2 difference 1). Under
 *     Capacitor the origin is `capacitor://localhost`, so a relative path
 *     resolves against the local scheme and fails. Enforced here rather than
 *     discovered on the first native build.
 */
import {
  zBumpItemVersionResponse,
  zCompleteSessionResponse,
  zCreateSessionResponse,
  zGetMeResponse,
  zGetPlanResponse,
  zGetSessionResponse,
  zGetVapidPublicKeyResponse,
  zLoginStubResponse,
  zPrepareSessionResponse,
  zSendTestPushResponse,
  zSubmitResponsesResponse,
  zSubscribeResponse,
  type BumpItemVersionResponse,
  type CompleteSessionResponse,
  type CreateSessionRequest,
  type CreateSessionResponse,
  type GetMeResponse,
  type GetPlanResponse,
  type GetSessionResponse,
  type GetVapidPublicKeyResponse,
  type LoginStubResponse,
  type OutgoingResponse,
  type PrepareSessionResponse,
  type PushSubscriptionInput,
  type SendTestPushResponse,
  type SubmitResponsesResponse,
  type SubscribeResponse,
} from './contract'

/** §7.5, driven live from the diagnostics screen. */
export interface InjectionConfig {
  latencyMs: number
  failureRate: number
  holdForever: boolean
}

export const NO_INJECTION: InjectionConfig = { latencyMs: 0, failureRate: 0, holdForever: false }

export interface ServerTimeSample {
  /** Epoch ms on the server clock, from the `X-Server-Time` header. */
  serverTimeMs: number
  /** `Date.now()` when the response arrived, so §11's offset can be computed. */
  receivedAtMs: number
}

export interface ApiClient {
  loginStub(displayName?: string): Promise<LoginStubResponse>
  getMe(): Promise<GetMeResponse>
  getPlan(): Promise<GetPlanResponse>
  /** §5.1: called on screen entry, before the student taps Start. */
  prepareSession(descriptorId: string): Promise<PrepareSessionResponse>
  /** The grant token arrives here once; `getSession` does not repeat it. */
  createSession(input: CreateSessionRequest): Promise<CreateSessionResponse>
  getSession(sessionId: string): Promise<GetSessionResponse>
  submitResponses(input: {
    sessionId: string
    grantToken: string
    responses: OutgoingResponse[]
  }): Promise<SubmitResponsesResponse>
  completeSession(input: { sessionId: string; grantToken: string }): Promise<CompleteSessionResponse>
  /** §7.5 development affordance: bump an item's version to exercise version conflicts. */
  bumpItemVersion(itemId: string): Promise<BumpItemVersionResponse>

  /**
   * Served rather than baked into the build: the API base URL is a build-time
   * constant across four .env files, so a compiled-in VAPID key could only be
   * rotated by rebuilding the PWA *and* shipping a new APK to every device — a
   * rotation nobody would ever actually perform.
   */
  getVapidPublicKey(): Promise<GetVapidPublicKeyResponse>
  registerPushSubscription(input: PushSubscriptionInput): Promise<SubscribeResponse>
  /**
   * Idempotent: removing an unknown handle succeeds. The client routinely holds
   * a stale one after the OS rotates an FCM token or the browser recreates a
   * subscription, and a 404 there is noise rather than information.
   */
  unregisterPushSubscription(input: { endpoint?: string; token?: string }): Promise<void>
  /** §7.5 development affordance, driven from the diagnostics screen. */
  sendTestPush(input: { message: string; url?: string }): Promise<SendTestPushResponse>

  setAccessToken(token: string | null): void
  setInjection(config: InjectionConfig): void
  /** Last `X-Server-Time` observed, paired with local receipt time (§11). */
  lastServerTime(): ServerTimeSample | null
  onServerTime(listener: (sample: ServerTimeSample) => void): () => void
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Worth retrying from the sync queue; a 4xx generally is not. */
  get retryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500
  }
}

export interface HttpApiOptions {
  baseUrl: string
  /** Web build sends the refresh cookie; mobile build uses bearer only (§13.2 difference 3). */
  credentials?: RequestCredentials
  fetchImpl?: typeof fetch
}

export function createHttpApi(options: HttpApiOptions): ApiClient {
  const baseUrl = normaliseBaseUrl(options.baseUrl)
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis)
  const credentials = options.credentials ?? 'include'

  let accessToken: string | null = null
  let injection: InjectionConfig = NO_INJECTION
  let lastSample: ServerTimeSample | null = null
  const listeners = new Set<(sample: ServerTimeSample) => void>()

  async function request(
    method: string,
    path: string,
    init: { body?: unknown; grantToken?: string } = {},
  ): Promise<{ data: unknown; servedAt: number }> {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (init.body !== undefined) headers['Content-Type'] = 'application/json'
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    // The session grant, not the access token, is what authorises submission.
    if (init.grantToken) headers['X-Session-Grant'] = init.grantToken
    if (injection.latencyMs > 0) headers['X-Poc-Latency-Ms'] = String(injection.latencyMs)
    if (injection.failureRate > 0) headers['X-Poc-Failure-Rate'] = String(injection.failureRate)
    if (injection.holdForever) headers['X-Poc-Hold'] = '1'

    let res: Response
    try {
      res = await doFetch(`${baseUrl}${path}`, {
        method,
        headers,
        credentials,
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
      })
    } catch (cause) {
      // Offline, DNS failure, connection reset — status 0 means "unknown outcome",
      // which idempotency on the response id (§7.3) makes safe to retry.
      throw new ApiError(`Network request failed: ${method} ${path}`, 0, cause)
    }

    recordServerTime(res)

    const text = await res.text()
    const parsed: unknown = text.length > 0 ? safeJson(text) : null

    if (!res.ok) {
      throw new ApiError(`${method} ${path} failed with ${res.status}`, res.status, parsed)
    }

    return { data: parsed, servedAt: lastSample?.serverTimeMs ?? Date.now() }
  }

  function recordServerTime(res: Response): void {
    const raw = res.headers.get('X-Server-Time')
    if (!raw) return
    const serverTimeMs = Number(raw)
    if (!Number.isFinite(serverTimeMs)) return
    lastSample = { serverTimeMs, receivedAtMs: Date.now() }
    for (const listener of listeners) listener(lastSample)
  }

  return {
    async loginStub(displayName) {
      const { data } = await request('POST', '/auth/login-stub', { body: { displayName } })
      return zLoginStubResponse.parse(data)
    },

    async getMe() {
      const { data } = await request('GET', '/me')
      return zGetMeResponse.parse(data)
    },

    async getPlan() {
      const { data } = await request('GET', '/plan')
      return zGetPlanResponse.parse(data)
    },

    async prepareSession(descriptorId) {
      const { data } = await request('POST', '/sessions/prepare', { body: { descriptorId } })
      return zPrepareSessionResponse.parse(data)
    },

    async createSession({ descriptorId, preparationId, elapsedSecOverride }) {
      const { data } = await request('POST', '/sessions', {
        body: { descriptorId, preparationId, elapsedSecOverride },
      })
      return zCreateSessionResponse.parse(data)
    },

    async getSession(sessionId) {
      const { data } = await request('GET', `/sessions/${encodeURIComponent(sessionId)}`)
      return zGetSessionResponse.parse(data)
    },

    async submitResponses({ sessionId, grantToken, responses }) {
      const { data } = await request('POST', `/sessions/${encodeURIComponent(sessionId)}/responses`, {
        body: { responses },
        grantToken,
      })
      return zSubmitResponsesResponse.parse(data)
    },

    async completeSession({ sessionId, grantToken }) {
      const { data } = await request('POST', `/sessions/${encodeURIComponent(sessionId)}/complete`, {
        body: {},
        grantToken,
      })
      return zCompleteSessionResponse.parse(data)
    },

    async bumpItemVersion(itemId) {
      const { data } = await request('POST', `/dev/items/${encodeURIComponent(itemId)}/bump-version`, {
        body: {},
      })
      return zBumpItemVersionResponse.parse(data)
    },

    async getVapidPublicKey() {
      const { data } = await request('GET', '/push/vapid-public-key')
      return zGetVapidPublicKeyResponse.parse(data)
    },

    async registerPushSubscription(input) {
      const { data } = await request('POST', '/push/subscriptions', { body: input })
      return zSubscribeResponse.parse(data)
    },

    async unregisterPushSubscription({ endpoint, token }) {
      // Query parameters rather than a DELETE body: a body on DELETE is legal
      // but ASP.NET Core's minimal APIs refuse to infer one, and proxies drop
      // it — and a silently ignored unsubscribe leaves the server pushing at a
      // dead handle for ever.
      const query = new URLSearchParams()
      if (endpoint) query.set('endpoint', endpoint)
      if (token) query.set('token', token)
      await request('DELETE', `/push/subscriptions?${query.toString()}`)
    },

    async sendTestPush({ message, url }) {
      const query = new URLSearchParams({ msg: message })
      if (url) query.set('url', url)
      const { data } = await request('GET', `/dev/push?${query.toString()}`)
      return zSendTestPushResponse.parse(data)
    },

    setAccessToken(token) {
      accessToken = token
    },

    setInjection(config) {
      injection = config
    },

    lastServerTime() {
      return lastSample
    },

    onServerTime(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

function normaliseBaseUrl(raw: string): string {
  if (!/^https?:\/\//i.test(raw)) {
    throw new Error(
      `VITE_API_BASE_URL must be an absolute URL, got "${raw}". ` +
        'Under Capacitor the app origin is capacitor://localhost, so a relative path ' +
        'resolves against the local scheme and fails (spec §13.2).',
    )
  }
  return raw.replace(/\/+$/, '')
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}
